import type { ReactElement } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { fetchDashboard } from '../api/endpoints';
import { queryKeys } from '../api/query-keys';
import { useSession } from '../auth/session-context';
import { useLiveEvents } from '../live/live-events';
import { LIVE_STREAM_LABEL, LIVE_STREAM_TONE, ROLE_LABEL } from '../domain/labels';
import { ModeBadge } from './ModeBadge';
import { StatusPill } from './StatusPill';

export interface NavItem {
  readonly to: string;
  readonly label: string;
  readonly glyph: string;
  readonly end?: boolean;
}

/** The seven sections, in the order an operator works through them. */
export const NAV_ITEMS: readonly NavItem[] = [
  { to: '/', label: 'Обзор', glyph: '◎', end: true },
  { to: '/strategy', label: 'Стратегия', glyph: '⚙' },
  { to: '/positions', label: 'Позиции и ордера', glyph: '▤' },
  { to: '/decisions', label: 'История решений', glyph: '☰' },
  { to: '/control', label: 'Управление', glyph: '⏻' },
  { to: '/backtest', label: 'Бэктест', glyph: '∿' },
  { to: '/audit', label: 'Аудит и подключения', glyph: '🛡' },
];

export function AppLayout(): ReactElement {
  const { session, signOut } = useSession();
  const live = useLiveEvents();
  const dashboard = useQuery({
    queryKey: queryKeys.dashboard,
    queryFn: ({ signal }) => fetchDashboard(signal),
  });

  const mode = dashboard.data?.mode ?? null;

  return (
    <div className="app-shell">
      <header className="app-header">
        <div className="app-header__brand">
          <span className="app-header__title">Панель управления торговым ботом</span>
          <span className="app-header__subtitle">BTC-USDT перпетуал</span>
        </div>

        <div className="app-header__meta">
          {mode === null ? (
            <StatusPill tone="warn" srPrefix="Режим работы">
              Режим неизвестен
            </StatusPill>
          ) : (
            <ModeBadge mode={mode} />
          )}

          <StatusPill tone={LIVE_STREAM_TONE[live.state]} srPrefix="Обновления">
            {LIVE_STREAM_LABEL[live.state]}
          </StatusPill>

          {session !== null && (
            <span className="live-indicator">
              {session.actor.email} · {ROLE_LABEL[session.actor.role]}
            </span>
          )}

          <button
            type="button"
            className="btn"
            onClick={() => {
              void signOut();
            }}
          >
            Выйти
          </button>
        </div>
      </header>

      <div className="app-body">
        <nav className="app-nav" aria-label="Разделы панели">
          <ul className="app-nav__list">
            {NAV_ITEMS.map((item) => (
              <li key={item.to}>
                <NavLink className="app-nav__link" to={item.to} end={item.end ?? false}>
                  <span className="app-nav__glyph" aria-hidden="true">
                    {item.glyph}
                  </span>
                  <span>{item.label}</span>
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        <main className="app-main">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
